import { useCallback, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'hooks';
import { upsertWaitlistEntry } from 'reducers/waitlist';
import { joinWaitlist } from 'services/waitlistService';
import type { WaitlistEntry, WaitlistGameType } from 'types/waitlist';
import { clearRecentlyLeftWaitlist } from 'utils/recentlyLeftWaitlists';

interface JoinArgs {
  gameId: number;
  gameType: WaitlistGameType;
  // Existing queue length, so the optimistic placeholder lands at the END of
  // the line (position/count = waitingCount + 1). Defaults to 0 → first in line.
  waitingCount?: number;
}

export interface JoinWaitlistResult {
  ok: boolean;
  error?: unknown;
}

/**
 * Shared "join the waitlist" action used by every CTA that offers it
 * (lobby tile, the standalone button, the colluder integrity modal). Owns the
 * full sequence — call the engine, build the optimistic entry when the POST
 * returns no body, clear the just-left guard, and dispatch the upsert — plus
 * the `pending` flag and the player-guid guard. Callers keep their own
 * success/failure UI (toast, alert, modal dismiss) and react to the returned
 * result.
 *
 * Previously this block was copy-pasted across JoinWaitlistButton, CashGameTile
 * and IntegrityBlockModal; centralizing it keeps the optimistic shape and the
 * side-effects in one place.
 */
export const useJoinWaitlist = () => {
  const dispatch = useAppDispatch();
  const playerGuid = useAppSelector((state) => state.user.playerGUID) || '';
  const [pending, setPending] = useState(false);

  const join = useCallback(
    async ({ gameId, gameType, waitingCount = 0 }: JoinArgs): Promise<JoinWaitlistResult> => {
      // Guard: a concurrent join, missing identity, or a non-numeric game id
      // (e.g. an SnG whose tableId is NaN) all short-circuit without a request.
      if (pending || !playerGuid || !Number.isFinite(gameId)) return { ok: false };
      setPending(true);
      try {
        const entry = await joinWaitlist({ gameId, gameType, playerGuid, autoBuyIn: 0 });
        const count = waitingCount + 1;
        const optimistic: WaitlistEntry = entry ?? {
          id: `pending-${gameId}-${playerGuid}`,
          playerGuid,
          gameId,
          gameType,
          position: count,
          waitlistCount: count,
          autoBuyIn: 0,
          yourTurn: false,
          availableSeats: 0,
          waitlistTimeOut: 30,
          waitForAcceptStartTime: null,
          bumpCount: 0
        };
        // Re-joining clears any just-left guard so this fresh spot is honoured by
        // the next broadcast instead of being suppressed.
        clearRecentlyLeftWaitlist(gameId);
        dispatch(upsertWaitlistEntry({ playerGuid, entry: optimistic }));
        return { ok: true };
      } catch (error) {
        return { ok: false, error };
      } finally {
        setPending(false);
      }
    },
    [pending, playerGuid, dispatch]
  );

  return { join, pending, playerGuid };
};

export default useJoinWaitlist;
